import { Injectable } from '@angular/core';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { PrimeTableComponent } from '../component/prime-table/prime-table.component';
import { ColumnDefinition } from '../model/column-definition';
import { ColumnSettings } from '../model/column-settings';
import { ColumnVisabilityPositionSetting } from '../model/data-source';
import { ResourceUUIDVersionIdentifier } from 'src/app/common/model/resource-id-version-identifier';

@Injectable()
export class TableColumnsService {

  private readonly tableKey = '-prime-table-';
  private availableColumnsSettings = new Map<string, ColumnSettings>();

  constructor(private userProfile: UserProfileService) { }

  getDataTableColumnsSettings(dataSourceName: string): ColumnSettings {
    if (this.availableColumnsSettings.get(dataSourceName) != null)
      return this.availableColumnsSettings.get(dataSourceName);
    else
      return this.createColumnsSettings(dataSourceName);
  }

  createColumnsSettings(dataSourceName: string): ColumnSettings {
    const sizeSettings = this.getSettings(this.buildKey(ColumnSettingType.Size, dataSourceName));
    const positionSettings = this.getSettings(this.buildKey(ColumnSettingType.Position, dataSourceName));
    const visibilitySettings = this.getSettings(this.buildKey(ColumnSettingType.Visibility, dataSourceName));

    const allColumns = new Set([
      ...Object.keys(sizeSettings),
      ...Object.keys(positionSettings),
      ...Object.keys(visibilitySettings)
    ]);

    const mergedSettings: ColumnSettings = {};

    allColumns.forEach(columnName => {
      mergedSettings[columnName] = ({
        name: columnName,
        width: sizeSettings[columnName]?.width ?? null,
        position: positionSettings[columnName]?.position ?? null,
        visible: visibilitySettings[columnName]?.visible ?? null
      });
    });
    this.availableColumnsSettings.set(dataSourceName, mergedSettings);
    return mergedSettings;
  }

  private buildKey(type: ColumnSettingType, dataSourceName: string): string {
    return `${type}-${dataSourceName}`;
  }

  private getSettings(key: string): Record<string, any> {
    const value = localStorage.getItem(this.getItemKey(key));
    return value ? JSON.parse(value) : {};
  }

  updateDataTableColumnSize(dataSourceName: string, columnName: string, sizeDelta: number): ColumnSettings {
    const key = this.buildKey(ColumnSettingType.Size, dataSourceName);
    const currentSettings = this.getSettings(key);

    if (currentSettings[columnName]) {
      currentSettings[columnName].width += sizeDelta;
    } else {
      currentSettings[columnName] = { width: PrimeTableComponent.DEFAULT_COLUMN_WIDTH + sizeDelta };
    }

    localStorage.setItem(this.getItemKey(key), JSON.stringify(currentSettings));
    return this.createColumnsSettings(dataSourceName);
  }

  updateDataTableColumnsByPositonAndVisability(dataSourceName: string, namesToVisibility: ColumnVisabilityPositionSetting[]): ColumnSettings {
    const positionKey = this.buildKey(ColumnSettingType.Position, dataSourceName);
    const visibleKey = this.buildKey(ColumnSettingType.Visibility, dataSourceName);

    const currentPositionSettings = this.getSettings(positionKey);
    const currentVisibilitySettings = this.getSettings(visibleKey);

    namesToVisibility.forEach(setting => {
      currentPositionSettings[setting.columnName] = {
        ...currentPositionSettings[setting.columnName],
        position: setting.position
      };

      currentVisibilitySettings[setting.columnName] = {
        ...currentVisibilitySettings[setting.columnName],
        visible: setting.isVisible
      };
    });

    localStorage.setItem(this.getItemKey(positionKey), JSON.stringify(currentPositionSettings));
    localStorage.setItem(this.getItemKey(visibleKey), JSON.stringify(currentVisibilitySettings));

    return this.createColumnsSettings(dataSourceName);
  }

  updateDataTableColumnsPositions(dataSourceName: string, columnsAfterChange: ColumnDefinition[]): ColumnSettings {
    const positionKey = this.buildKey(ColumnSettingType.Position, dataSourceName);
    const currentPositionSettings = this.getSettings(positionKey);

    columnsAfterChange.forEach((col, idx) => {
      currentPositionSettings[col.name] = {
        ...currentPositionSettings[col.name],
        position: idx
      };
    });

    localStorage.setItem(this.getItemKey(positionKey), JSON.stringify(currentPositionSettings));
    return this.createColumnsSettings(dataSourceName);
  }

  // count = 0 oznacza przypiętą tylko kolumnę "options"; null wyłącza przypinanie
  updateFrozenColumnsCount(dataSourceName: string, count: number | null): void {
    const key = this.getItemKey(this.buildKey(ColumnSettingType.Frozen, dataSourceName));
    if (count != null)
      localStorage.setItem(key, String(count));
    else
      localStorage.removeItem(key);
  }

  getFrozenColumnsCount(dataSourceName: string): number | null {
    const raw = localStorage.getItem(this.getItemKey(this.buildKey(ColumnSettingType.Frozen, dataSourceName)));
    const parsed = raw != null ? parseInt(raw, 10) : NaN;
    return isNaN(parsed) ? null : parsed;
  }

  resetColumnsSettings(dataSourceName: string): void {
    const keysToClear = [
      this.buildKey(ColumnSettingType.Size, dataSourceName),
      this.buildKey(ColumnSettingType.Position, dataSourceName),
      this.buildKey(ColumnSettingType.Visibility, dataSourceName),
      this.buildKey(ColumnSettingType.Grouping, dataSourceName),
      this.buildKey(ColumnSettingType.ColumnsSetId, dataSourceName),
      this.buildKey(ColumnSettingType.MinimapHeight, dataSourceName),
      this.buildKey(ColumnSettingType.Frozen, dataSourceName),
      this.buildKey(ColumnSettingType.PaginationHidden, dataSourceName)
    ];

    keysToClear.forEach(key => {
      localStorage.removeItem(this.getItemKey(key));
    });
    this.clearAvailableColumsSettingsCache(dataSourceName);
  }

  updateGroupingColumn(dataSourceName: string, groupingColumnName?: string): void {
    const key = this.buildKey(ColumnSettingType.Grouping, dataSourceName);
    if (groupingColumnName)
      localStorage.setItem(this.getItemKey(key), groupingColumnName);
    else
      this.removeGroupingColumn(dataSourceName);
  }

  private removeGroupingColumn(dataSourceName: string): void {
    const key = this.buildKey(ColumnSettingType.Grouping, dataSourceName);
    localStorage.removeItem(this.getItemKey(key));
  }

  getGroupingColumn(dataSourceName: string): string | null {
    const key = this.buildKey(ColumnSettingType.Grouping, dataSourceName);
    return localStorage.getItem(this.getItemKey(key));
  }

  setColumnsSetId(dataSourceName: string, setId: ResourceUUIDVersionIdentifier): void {
    const key = this.buildKey(ColumnSettingType.ColumnsSetId, dataSourceName);
    localStorage.setItem(this.getItemKey(key), setId.toString());
  }

  getColumnsSetId(dataSourceName: string): ResourceUUIDVersionIdentifier | null {
    const key = this.buildKey(ColumnSettingType.ColumnsSetId, dataSourceName);
    const stringValue = localStorage.getItem(this.getItemKey(key));
    return stringValue != null ? ResourceUUIDVersionIdentifier.createFromString(stringValue) : null;
  }

  resetColumnsSetId(dataSourceName: string): void {
    const key = this.buildKey(ColumnSettingType.ColumnsSetId, dataSourceName);
    localStorage.removeItem(this.getItemKey(key));
  }


  // ─── View preferences (global — one object for all tables) ─────────────────

  /** Preferencje widoku: które elementy UI są widoczne (globalne dla wszystkich tabel). */
  getViewPrefs(): ViewPrefs {
    const raw = localStorage.getItem(this.getGlobalKey('view-prefs'));
    try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
  }

  /** Zapisuje (merguje) preferencje widoku. */
  saveViewPrefs(partial: Partial<ViewPrefs>): void {
    const merged = { ...this.getViewPrefs(), ...partial };
    localStorage.setItem(this.getGlobalKey('view-prefs'), JSON.stringify(merged));
  }

  // ─── Pagination visibility (per-table) ──────────────────────────────────────

  /** Czy użytkownik ukrył pasek paginacji dla danej tabeli. */
  getPaginationHidden(dataSourceName: string): boolean {
    return localStorage.getItem(this.getItemKey(this.buildKey(ColumnSettingType.PaginationHidden, dataSourceName))) === 'true';
  }

  /** Zapisuje wybór ukrycia paska paginacji dla danej tabeli. */
  savePaginationHidden(dataSourceName: string, hidden: boolean): void {
    const key = this.getItemKey(this.buildKey(ColumnSettingType.PaginationHidden, dataSourceName));
    if (hidden)
      localStorage.setItem(key, 'true');
    else
      localStorage.removeItem(key);
  }

  // ─── Minimap height (per-table) ──────────────────────────────────────────────

  /** Zwraca zapisaną wysokość minimap dla danej tabeli lub null gdy brak zapisu. */
  getMinimapHeight(dataSourceName: string): number | null {
    const raw = localStorage.getItem(this.getItemKey(this.buildKey(ColumnSettingType.MinimapHeight, dataSourceName)));
    if (raw === null) return null;
    const n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  /** Zapisuje wysokość minimap dla danej tabeli. */
  saveMinimapHeight(dataSourceName: string, height: number): void {
    localStorage.setItem(
      this.getItemKey(this.buildKey(ColumnSettingType.MinimapHeight, dataSourceName)),
      String(Math.round(height))
    );
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  private getItemKey(key: string): string {
    return this.userProfile.getUserProfile()?.username + this.tableKey + key;
  }

  /** Klucz globalny — wspólny dla wszystkich tabel danego użytkownika. */
  private getGlobalKey(suffix: string): string {
    return (this.userProfile.getUserProfile()?.username ?? '') + this.tableKey + suffix;
  }

  clearAvailableColumsSettingsCache(dataSourceName: string): void {
    this.availableColumnsSettings.delete(dataSourceName);
  }

}

/** Preferencje widoku — globalne, niezależne od konkretnej tabeli. */
export interface ViewPrefs {
  minimap?: boolean; // czy minimap jest widoczna
  ruler?: boolean; // czy linijka wierszy (row ruler) jest widoczna
  filterBar?: boolean; // czy pasek filtrów jest widoczny
}

enum ColumnSettingType {
  Size = 'cols-size',
  Position = 'cols-position',
  Visibility = 'cols-visible',
  Grouping = 'grouping-col',
  ColumnsSetId = 'columns-set-id',
  MinimapHeight = 'minimap-height',
  Frozen = 'frozen-cols-count',
  PaginationHidden = 'pagination-hidden'
}

